import React from 'react';
import { Camera, Brain, FileText, Home, DollarSign, Download, Shield, Clock, CheckCircle } from 'lucide-react';
import Reveal from './motion/Reveal';

const Features = () => {
  const features = [
    {
      icon: Camera,
      title: 'Multi-Item Smart Scanning',
      description: 'The app analyzes multiple items within each image, quickly creating a detailed inventory of your contents with estimated replacement cost for each item. No need to photograph items individually.',
      benefit: 'Complete inventory in minutes',
      span: 'lg:col-span-7',
      featured: true
    },
    {
      icon: Brain,
      title: 'AI-Powered Analysis',
      description: 'Photo metadata captures when and where images were taken, proving photos are genuine and not downloaded.',
      benefit: 'Undisputable proof of ownership',
      span: 'lg:col-span-5'
    },
    {
      icon: FileText,
      title: 'Smart Receipt Sync',
      description: 'Add copies of your receipts and the app automatically syncs them with matching items in your inventory or adds new items as required.',
      benefit: 'Effortless documentation',
      span: 'lg:col-span-5'
    },
    {
      icon: Home,
      title: 'Room-Based Organization',
      description: 'Organize your inventory by room for easy navigation and management. Find what you need, when you need it.',
      benefit: 'Intuitive structure',
      span: 'lg:col-span-7'
    },
    {
      icon: DollarSign,
      title: 'Replacement Value Calculator',
      description: 'Calculates total replacement value to help set an accurate sum insured — no more guessing.',
      benefit: 'Prevents under-insurance',
      span: 'lg:col-span-6'
    },
    {
      icon: Download,
      title: 'Your Data, Your Way',
      description: 'Nobody knows your home better than you! Edit items directly in the app, or export to Excel, edit and store your list anywhere, then import the updated list back into the app.',
      benefit: 'Complete flexibility',
      span: 'lg:col-span-6'
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Created Before Loss',
      description: 'Document everything when evidence is clean and stress is low'
    },
    {
      icon: Clock,
      title: 'Fast Enough to Actually Use',
      description: 'Most people skip contents inventory because it\'s tedious. We made it fast.'
    },
    {
      icon: CheckCircle,
      title: 'From Memory to Evidence',
      description: 'Transform claims from guesswork into documented, evidenced records'
    }
  ];

  return (
    <section id="features" data-testid="features-section" className="py-28 bg-ink border-t rule-dark">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <Reveal>
          <div className="max-w-3xl mb-16">
            <p className="eyebrow text-cyan-400 mb-5">Powerful features</p>
            <h2 className="text-4xl lg:text-6xl font-bold text-white leading-tight">
              Everything you need to protect what matters
            </h2>
            <p className="mt-6 text-lg md:text-xl text-slate-400 leading-relaxed">
              VerifAEye makes comprehensive contents documentation effortless. No tedious spreadsheets, no hours of manual entry.
            </p>
          </div>
        </Reveal>

        {/* Bento grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-12 gap-5 mb-24">
          {features.map((feature, index) => (
            <Reveal key={index} delay={(index % 3) * 0.1} className={`md:col-span-1 ${feature.span} h-full`}>
              <div
                data-testid={`feature-card-${index}`}
                className={`group relative h-full rounded-3xl border p-8 transition-all duration-500 hover:-translate-y-1.5 ${
                  feature.featured
                    ? 'border-cyan-400/30 bg-gradient-to-br from-cyan-500/10 via-ink-soft to-emerald-500/10'
                    : 'border-white/10 bg-white/[0.03] hover:border-cyan-400/30'
                }`}
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-emerald-400 rounded-2xl flex items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                    <feature.icon className="w-6 h-6 text-ink" />
                  </div>
                  <span className="eyebrow text-slate-600">0{index + 1}</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-400 leading-relaxed mb-6">{feature.description}</p>
                <div className="mt-auto flex items-center gap-2 text-emerald-400 font-medium">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">{feature.benefit}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Why VerifAEye is different */}
        <Reveal>
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-10 lg:p-14 mb-24">
            <p className="eyebrow text-emerald-400 text-center mb-4">The difference</p>
            <h3 className="text-3xl lg:text-4xl font-bold text-white text-center mb-14">
              Why VerifAEye is different
            </h3>
            <div className="grid md:grid-cols-3 gap-10">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center space-y-4 group">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-emerald-400 rounded-2xl flex items-center justify-center mx-auto transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-3">
                    <benefit.icon className="w-8 h-8 text-ink" />
                  </div>
                  <h4 className="text-xl font-bold text-white">{benefit.title}</h4>
                  <p className="text-slate-400 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Problem/Solution statement */}
        <Reveal>
          <div className="max-w-4xl mx-auto rounded-3xl border border-white/10 bg-gradient-to-br from-ink-soft to-ink p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-cyan-500/15 rounded-full blur-[100px]"></div>
            <div className="relative z-10 space-y-6">
              <p className="eyebrow text-cyan-400">The problem we solve</p>
              <h3 className="text-3xl lg:text-4xl font-bold text-white">Why most people struggle with insurance claims</h3>
              <p className="text-lg text-slate-400 leading-relaxed">
                Most people don't really understand insurance or engage with it until they need to claim. 
                As a result, many are under-insured and completely unprepared. They lack evidence of what they owned.
              </p>
              <p className="text-lg text-slate-400 leading-relaxed">
                Then, when they're already battling to cope with the incident that caused them to lodge a claim, 
                they have to hunt through receipts and photos, trying to remember everything they had that is now lost. 
                They might miss things, forget to claim them, or be unable to prove items they received as gifts.
              </p>
              <div className="pt-6 border-t rule-dark">
                <p className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                  <a href="https://verifaeye.com.au" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">VerifAEye</a> helps you be prepared before you need to claim.
                </p>
                <p className="text-slate-400 mt-3">
                  When you understand what you need to document and have it ready, insurance works the way it should — as a true partnership.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Features;
